import { useCallback, useEffect, useMemo, useState } from 'react';
import { usePageLoading } from './motion/PetalLoader';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Check, ChevronLeft, ChevronRight, UserPlus } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import PageBackHeader from './ui/PageBackHeader';
import Button from './ui/Button';
import GroupInfoPanel, { GroupNameHeading } from './chat/GroupInfoPanel';
import GroupInfoModals from './chat/GroupInfoModals';
import useGroupInfoPage from '../hooks/useGroupInfoPage';
import { useEventLineup, useMemberAttendanceDays } from '../hooks/queries/useEventLineup';
import { festivalDaysFromLineup } from '../utils/lineupFormat';
import GroupMembersList from './chat/GroupMembersList';
import { isFestivalEvent } from '../utils/eventType';
import { GLASS_SURFACES, CARD_SOLID_SURFACE_CLASS } from '../config/glassSurfaces';
import { isGenericGroupName } from '../lib/proximity/data/userGroups';
import { resolveEventDisplayName } from '../utils/eventDisplayName';
import { goBackTo, previousPath } from '../lib/navHistory';
import Avatar from './Avatar';
import { markDone, useNudge } from '../lib/moments';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../supabaseClient';
import { markMapTap } from '../lib/map/openTiming';
import { appLog } from '../lib/appLog';

const cardClass = `event-card-surface overflow-hidden rounded-[18px] p-4 sm:p-6 ${GLASS_SURFACES.cards ? '' : CARD_SOLID_SURFACE_CLASS}`;

/**
 * What someone who is not in the group sees: the one action that matters. A shared link lands here (chat is off,
 * so /groups/:id is this page), and before 2026-09-23 there was no way to join from it at all.
 */
function GroupJoinCard({ info, signedIn, onBack = null }) {
  // Auto-named groups ("Group 1") mean nothing to the friend a link reaches; the festival does.
  const eventName = info.event ? resolveEventDisplayName(info.event) : null;
  const title =
    info.groupDisplayName && !isGenericGroupName(info.groupDisplayName)
      ? info.groupDisplayName
      : eventName
        ? `${eventName} group`
        : info.groupDisplayName || 'this group';
  let body;
  let action;
  if (!signedIn) {
    // One Join (Sam, 2026-10-23: no "I have an account", no paragraph): the sign-in pop-up has the Log in | Sign up switch.
    body = null;
    action = (
      <Button
        size="lg"
        fullWidth
        onClick={() => info.signInToJoin(title)}
        leftIcon={<UserPlus className="h-4 w-4" aria-hidden />}
      >
        Join
      </Button>
    );
  } else if (info.isJoinClosed) {
    body = 'Joining is closed for this event.';
  } else if (info.joinRequestSent) {
    body = 'Request sent. You’ll get a notification when a group manager lets you in.';
  } else {
    body = info.canJoinDirectly
      ? 'Join to see each other on the festival map and in Find.'
      : 'A manager approves requests.'; // Sam's A, 2026-10-26: one line, no invite-link tip;
    action = (
      <Button
        size="lg"
        fullWidth
        loading={info.joining}
        onClick={info.joinGroup}
        leftIcon={<UserPlus className="h-4 w-4" aria-hidden />}
      >
        {info.canJoinDirectly ? 'Join group' : 'Request to join'}
      </Button>
    );
  }
  return (
    <div className={`${cardClass} mb-6`}>
      {/* Back lives on this line when there is somewhere to go back to (Sam, 2026-10-23: not a row of its own). */}
      <div className="mb-1 flex items-center gap-2">
        {onBack && (
          <button
            type="button"
            className="-ml-1 grid h-7 w-7 shrink-0 place-items-center rounded-full bg-white/5 text-dark-text"
            onClick={onBack}
            aria-label="Back"
          >
            <ChevronLeft size={16} aria-hidden="true" />
          </button>
        )}
        {/* Only an invite link is an invitation (Sam, 2026-10-23: a group found in the Groups tab said "You're invited"). */}
        <p className="text-xs font-semibold uppercase tracking-wide text-primary">
          {info.inviteCode ? 'You’re invited' : info.isPublicGroup ? 'Open group' : 'Private group'}
        </p>
      </div>
      <h2 className="glass-title-emphasis mb-1 text-xl font-bold text-gray-900 dark:text-dark-text">
        {title}
      </h2>

      {body ? (
        <p className="mb-4 text-sm text-gray-600 dark:text-dark-muted">{body}</p>
      ) : (
        <div className="mb-4" />
      )}
      {action}
    </div>
  );
}

/**
 * Who runs the group, for someone deciding whether to join it (Sam, 2026-10-23: "at least one point of reference before you
 * join"): the full member count, and the creator and managers by name and photo - from group_invite_preview (161), since
 * someone signed out cannot read a group's members. Nobody else is named, and nothing of the group's - chat, picks,
 * locations - shows until you are in.
 */
function GroupLeads({ preview }) {
  const count = preview?.member_count ?? 0;
  const leads = preview?.leads ?? [];
  if (!count && !leads.length) return null;
  return (
    <section className="mt-6">
      <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-dark-muted">
        Members · {count}
      </h2>
      <ul className={`${cardClass} flex flex-col gap-3`}>
        {leads.map((m) => (
          <li key={m.user_id} className="flex items-center gap-3">
            <Avatar displayName={m.display_name} profilePhotoUrl={m.profile_photo_url} size="sm" />
            <span className="min-w-0 flex-1 truncate text-[15px] font-semibold text-dark-text">
              {m.display_name || 'Member'}
            </span>
            <span className="shrink-0 text-xs font-semibold text-dark-muted">
              {m.role === 'creator' ? 'Creator' : 'Manager'}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

const STEPS_KEY = (groupId) => `prii.groupSteps.${groupId}`;
function readSteps(groupId) {
  try {
    return JSON.parse(window.localStorage.getItem(STEPS_KEY(groupId)) || '{}');
  } catch {
    return {};
  }
}
function writeSteps(groupId, steps) {
  try {
    window.localStorage.setItem(STEPS_KEY(groupId), JSON.stringify(steps));
  } catch {
    /* private mode */
  }
}

/**
 * After creating or joining a group, the four things the weekend runs on, each one tap away (operator, 2026-09-23:
 * "walk them through ... so they know the features exist"). Ticks are per device; the card leaves once all are done
 * or dismissed.
 */
function GroupNextSteps({ groupId, eventId, memberCount, shared, onInvite }) {
  const navigate = useNavigate();
  const [done, setDone] = useState(() => readSteps(groupId));
  const mark = (key) =>
    setDone((prev) => {
      const next = { ...prev, [key]: true };
      writeSteps(groupId, next);
      return next;
    });
  const steps = [
    {
      key: 'invite',
      label: 'Invite your friends',
      done: done.invite || shared || memberCount > 1,
      run: onInvite,
    },
    {
      key: 'sets',
      label: 'Pick the sets you want to see',
      done: done.sets,
      run: () =>
        navigate(`/sets/${encodeURIComponent(eventId)}?group=${encodeURIComponent(groupId)}`, {
          state: { backTo: `/groups/${groupId}/info` },
        }),
    },
    {
      key: 'map',
      label: 'Look around the festival map',
      done: done.map,
      run: () => {
        markMapTap();
        navigate(`/map/${encodeURIComponent(eventId)}`);
      },
    },
    ...(Capacitor.isNativePlatform()
      ? [
          {
            key: 'find',
            label: 'Try Find on a friend',
            done: done.find,
            run: () => navigate('/find'),
          },
        ]
      : []),
  ];
  if (done.dismissed || steps.every((st) => st.done)) return null;
  return (
    <section className={`${cardClass} mb-6`} aria-label="Next steps">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-base font-semibold text-gray-900 dark:text-dark-text">
          Get set for the weekend
        </h2>
        <button
          type="button"
          onClick={() => mark('dismissed')}
          className="text-xs text-gray-500 underline underline-offset-2 dark:text-dark-muted"
        >
          Hide
        </button>
      </div>
      <ul className="flex flex-col">
        {steps.map((st) => (
          <li key={st.key}>
            <button
              type="button"
              onClick={() => {
                mark(st.key);
                st.run();
              }}
              className="flex min-h-[44px] w-full items-center gap-3 text-left"
            >
              <span
                className={`grid h-5 w-5 shrink-0 place-items-center rounded-full border ${st.done ? 'border-[var(--glass-accent-ring)] bg-[image:var(--glass-accent-fill)] text-white' : 'border-white/30'}`}
                aria-hidden="true"
              >
                {st.done ? <Check className="h-3 w-3" /> : null}
              </span>
              <span
                className={`flex-1 text-sm ${st.done ? 'text-gray-500 line-through dark:text-dark-muted' : 'text-gray-900 dark:text-dark-text'}`}
              >
                {st.label}
              </span>
              <ChevronRight className="h-4 w-4 shrink-0 text-ink/40" aria-hidden />
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

export default function GroupInfoPage() {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const info = useGroupInfoPage(groupId);
  // The whole page at once: the group AND its event (the event card filled in after the rest - Sam, 2026-10-20).
  const pageLoad = usePageLoading(
    Boolean(info.session) && (info.loading || (Boolean(info.group?.event_id) && !info.event)),
    { latch: Boolean(info.session) },
  );
  // An expired group (event over, or archived) is read-only: no one sees rename, invite, access or member controls.
  const canManage = info.isManager && !info.isExpired;
  // A new group's Invite button glows until it is used (the button is the prompt; no second button).
  const justCreated = Boolean(location.state?.justCreated);
  // An auto-named group ("Group 3") opens the card's own name field, glowing and empty, instead of a separate
  // naming card (operator, 2026-09-23) - friends search by name, and "Group 3" finds nobody.
  const needsName = Boolean(
    !info.loading &&
    info.isManager &&
    !info.isExpired &&
    isGenericGroupName(info.groupDisplayName || ''),
  );
  const [nameOpened, setNameOpened] = useState(false);
  useEffect(() => {
    if (!needsName || nameOpened || info.isEditingManagerGroupName) return;
    setNameOpened(true);
    info.startEditingGroupName();
    info.setManagerGroupNameDraft('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsName, nameOpened, info.isEditingManagerGroupName]);
  const eventId = info.event?.event_id;
  // Only festivals have lineups. A single-artist concert can still have rows in
  // event_artists (that is how the headliner is recorded), so lineup.length was
  // true for ordinary concerts and put a Lineup button on their group — matching
  // what GroupMembersPage already does for its day dots.
  const isFestival = isFestivalEvent(info.event);
  const { data: lineup = [] } = useEventLineup(eventId, {
    enabled: Boolean(eventId && !info.loading) && isFestival,
  });
  const hasLineup = isFestival && lineup.length > 0;
  const festivalDays = useMemo(() => festivalDaysFromLineup(lineup), [lineup]);
  const memberIds = useMemo(
    () => info.members.filter((m) => !m.left_at).map((m) => m.user_id),
    [info.members],
  );
  const { data: attendanceByUser = {} } = useMemberAttendanceDays(eventId, memberIds, {
    enabled: festivalDays.length > 0,
  });

  // Who runs the group, for anyone not in it (signed out or not yet joined): group_invite_preview (161).
  const { data: invitePreview } = useQuery({
    queryKey: ['groups', 'invite-preview', groupId],
    enabled: Boolean(groupId) && (!info.session || (!info.loading && !info.isMember)),
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('group_invite_preview', { p_group_id: groupId });
      if (error) throw error;
      return data;
    },
  });

  // The first-time path (lib/moments): being in a group (made or joined) is the "group" step; Sets glows until the first pick.
  useEffect(() => {
    if (!info.loading && info.isMember) markDone('group');
  }, [info.loading, info.isMember]);
  // How long a group takes to open (Sam, 2026-11-01: "it took a while to load that group info"): mount to loaded, once
  const [openedAt] = useState(() => performance.now());
  const [timed, setTimed] = useState(false);
  useEffect(() => {
    if (timed || info.loading) return;
    setTimed(true);
    appLog('info', 'group.info_ready', {
      ms: Math.round(performance.now() - openedAt),
      member: Boolean(info.isMember),
      online: navigator.onLine,
    });
  }, [timed, info.loading, info.isMember, openedAt]);
  const setsGlow = useNudge('pick', hasLineup && Boolean(info.isMember));

  // A member row's action menu closes on a tap outside it (as on the old members page).
  useEffect(() => {
    if (!info.openActionsMemberId) return undefined;
    const handleOutside = (e) => {
      if (info.openActionsRowRef.current && !info.openActionsRowRef.current.contains(e.target)) {
        info.setOpenActionsMemberId(null);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, [info.openActionsMemberId, info.openActionsRowRef, info.setOpenActionsMemberId]);

  // Fixed hierarchy (chat > group info > members/lineup), never history-based.
  // A history pop here would incorrectly return to members/lineup after the user came back.
  const handleBack = useCallback(() => {
    goBackTo(navigate, info.chatPath);
  }, [navigate, info.chatPath]);

  // Signed out (an invite link): the group, one Join, its event as a member sees it, and who runs it (Sam, 2026-10-23).
  if (!info.session) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-6">
        {/* No "Group" bar - the card names the group - and Back only when there is somewhere in the app to go back to: a
            link from a text message has nothing behind it (Sam, 2026-10-23). */}
        <GroupJoinCard
          info={info}
          signedIn={false}
          onBack={previousPath() ? () => navigate(-1) : null}
        />
        {!info.loading && info.event && (
          <GroupInfoPanel
            eventForCard={info.eventForCard}
            group={info.group}
            isArchived={info.isExpired}
            isManager={false}
            isMember={false}
            hasLineup={hasLineup}
            onOpenLineup={() =>
              navigate(`/sets/${encodeURIComponent(eventId)}`, {
                state: { backTo: `${location.pathname}${location.search}` },
              })
            }
          />
        )}
        <GroupLeads preview={invitePreview} />
      </div>
    );
  }

  if (info.error) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-6">
        <PageBackHeader
          title={info.groupDisplayName}
          titlePending={info.headerTitlePending}
          onBack={handleBack}
          backLabel="Back"
        />
        <div className="rounded-lg bg-danger-fill p-4 text-danger">{info.error}</div>
      </div>
    );
  }
  // Offline with nothing saved for this group (never opened on this phone): one quiet line, not the red error.
  if (info.offline && !info.group) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-6">
        <PageBackHeader title={info.groupDisplayName} onBack={handleBack} backLabel="Back" />
        <p className="py-16 text-center text-[15px] text-[#94a3b8]">
          This group opens once you're back online.
        </p>
      </div>
    );
  }
  if (pageLoad.show) return pageLoad.node;

  return (
    <div className="mx-auto max-w-2xl px-4 py-4">
      {/* No title bar: the card below already names the group (operator, 2026-09-23: "the title twice"). Back stays,
          as the Maps page's round button - a group is reached from Sets, events and invite links, not only Groups. */}
      {/* Not in the group (an invite): the join card names the group and carries Back; no bar above it. */}
      {(info.loading || info.isMember) && (
        <div className="mb-4 flex min-h-[44px] items-center gap-3">
          <button type="button" className="maps-page__back" onClick={handleBack} aria-label="Back">
            <ChevronLeft size={18} aria-hidden="true" />
          </button>
          {!info.loading && info.isMember && (
            <GroupNameHeading
              displayGroupName={info.groupDisplayName || 'Group'}
              isArchived={info.isExpired}
              isManager={canManage}
              isEditingGroupName={info.isEditingManagerGroupName}
              groupNameDraft={info.managerGroupNameDraft}
              onGroupNameDraftChange={info.setManagerGroupNameDraft}
              groupRenameError={info.managerGroupRenameError}
              onGroupRenameErrorClear={() => info.setManagerGroupRenameError('')}
              savingGroupName={info.savingManagerGroupName}
              onStartEditingGroupName={info.startEditingGroupName}
              onCancelEditingGroupName={info.cancelEditingGroupName}
              onSaveGroupName={info.saveManagerGroupName}
              namePlaceholder="Group name"
            />
          )}
        </div>
      )}

      {!info.loading && !info.isMember && (
        <GroupJoinCard info={info} signedIn onBack={previousPath() ? handleBack : null} />
      )}

      {info.loading ? null : (
        <GroupInfoPanel
          eventForCard={info.eventForCard}
          group={info.group}
          isArchived={info.isExpired}
          isManager={canManage}
          isMember={info.isMember}
          isPrivateGroup={info.isPrivateGroupSetting}
          savingAccessType={info.savingManagerAccessType}
          onSetAccessType={info.updateManagerAccessType}
          hasLineup={hasLineup}
          setsGlow={setsGlow}
          // One set-times page, not two (operator, 2026-09-23): the Sets tab already shows a group's overlap, so the
          // group's "Sets" button opens it with this group picked. /groups/:id/lineup stays routable for old links.
          // Not in the group: the festival's own Sets - with ?group= Sets fell back to one of your OTHER groups for the
          // festival and showed its picks (Sam, 2026-10-23).
          onOpenLineup={() =>
            navigate(
              `/sets/${encodeURIComponent(eventId)}${info.isMember ? `?group=${encodeURIComponent(groupId)}` : ''}`,
              {
                state: { backTo: `/groups/${groupId}/info` },
              },
            )
          }
          shared={info.shared}
          onCopyShareLink={info.copyShareLink}
          pendingJoinRequests={info.pendingJoinRequests}
          onApproveJoinRequest={info.approveJoinRequest}
          onDenyJoinRequest={info.denyJoinRequest}
          highlightInvite={
            justCreated && !info.shared && info.visibleMemberCount <= 1 && !needsName
          }
          // Approve-new-members and Reset link stay in the database and the panel, not on screen, for now
          // (operator, 2026-09-23: "too much wording") - pass approveNewMembers/onSetApproveNewMembers/onResetInviteLink
          // to bring the section back.
        />
      )}

      {!info.loading && info.isMember && !info.isExpired && eventId && isFestival && (
        <GroupNextSteps
          groupId={groupId}
          eventId={eventId}
          memberCount={info.visibleMemberCount}
          shared={info.shared}
          onInvite={info.copyShareLink}
        />
      )}

      {!info.loading && !info.isMember && <GroupLeads preview={invitePreview} />}

      {/* Members live on the group page (operator, 2026-09-23: "less clicking"), the same list the members page had. */}
      {!info.loading && info.isMember && (
        <section className="mt-6">
          <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-dark-muted">
            Members · {info.visibleMemberCount}
          </h2>
          <GroupMembersList
            members={info.members}
            blockedUsers={info.blockedUsers}
            sessionUserId={info.userId}
            festivalDays={festivalDays}
            attendanceByUser={attendanceByUser}
            viewerAttendingDays={attendanceByUser[info.userId] || []}
            isManager={canManage}
            isDbUuid={info.isDbUuid}
            canLeaveGroup={info.canLeaveGroup}
            openActionsMemberId={info.openActionsMemberId}
            onOpenActionsMemberIdChange={info.setOpenActionsMemberId}
            openActionsRowRef={info.openActionsRowRef}
            onOpenMemberProfile={info.openMemberProfile}
            onMemberPhotoZoom={info.setMemberPhotoZoom}
            onPromoteMember={info.promoteMemberToManager}
            onRemoveMember={info.setShowRemoveModal}
            onBlockMember={info.setShowBlockModal}
            onUnblockMember={info.handleUnblockUser}
            onLeaveGroup={info.openLeaveGroupModal}
            formatDate={info.formatDate}
          />
        </section>
      )}

      <GroupInfoModals {...info} />
    </div>
  );
}
